import { Token } from '@kodiak-finance/sdk-core'
import { AutoColumn } from 'components/Column'
import CurrencyLogo from 'components/CurrencyLogo'
import FiatValue from 'components/FiatValue'
import Loader from 'components/Loader'
import MigrationBanner from 'components/MigrationBanner'
// import { ConfirmAddModalBottom } from './ConfirmAddModalBottom'
// import { PoolPriceBar } from './PoolPriceBar'
import PoweredByKodiak from 'components/PoweredByKodiak'
import { RowBetween } from 'components/Row'
import { isSupportedChain } from 'config/chains'
import { useToken } from 'hooks/Tokens'
import { useConnection } from 'hooks/useConnection'
import {
  useKodiakFarm,
  useKodiakFarmAverageApr,
  useKodiakFarmLockedStakesOf,
  useKodiakFarmTotalLocked,
} from 'hooks/useKodiakFarm'
import { useRewardVaultDatas } from 'hooks/useRewardVault'
import { useTokenBalance } from 'lib/hooks/useCurrencyBalance'
import IslandFarmEarnedCard from 'pages/Islands/IslandPage/IslandFarmEarnedCard'
import { useIsFarmAdmin } from 'pages/Liquidity/hooks/useIsFarmAdmin'
import { MainContentWrapper } from 'pages/Liquidity/Liquidity.styles'
import { useCallback, useContext } from 'react'
import { useMemo } from 'react'
import { AlertTriangle, ArrowLeft } from 'react-feather'
import styled, { css, ThemeContext } from 'styled-components'
import { ThemedText } from 'theme'
import { ExternalLink, StyledInternalLink } from 'theme/components'
import { KodiakFarm } from 'types/farms'
import { formatAmount } from 'utils/formatDollarAmt'
import { ExplorerDataType, getExplorerLink } from 'utils/getExplorerLink'
import { unwrappedToken } from 'utils/unwrappedToken'
import { Address } from 'viem'

import { REFETCH_POLICY_FAST } from '../../config/RefetchPolicies'
import IslandManagementCard from './IslandManagementCard'
import IslandPositionCard from './IslandPositionCard'
import * as S from './Styleds'

const TitleRow = styled(RowBetween)`
  color: ${({ theme }) => theme.deprecated_text2};
  ${({ theme }) => theme.mediaWidth.upToSmall`
    flex-wrap: wrap;
    gap: 12px;
    width: 100%;
  `};
`

const ErrorContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: auto;
  padding: 24px 0;
  max-width: 300px;
  /* min-height: 25vh; */
`

const IconStyle = css`
  width: 40px;
  height: 40px;
  margin-bottom: 0.5rem;
`

const NetworkIcon = styled(AlertTriangle)`
  ${IconStyle}
`

const StategyBadge = styled.div`
  background-color: ${({ theme }) => theme.cardSecondary};
  color: ${({ theme }) => theme.textSecondary};
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 12px;
  line-height: 20px;
  ${({ theme }) => theme.mediaWidth.upToSmall`
    font-size: 12px;
  `};
`

const ErrorWrapper = styled.div`
  height: 100%;
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: center;
`

type AprCardProps = {
  farm: KodiakFarm | null
  rewardVaultRewardApr: number | null
  stakingType: 'farm' | 'vault' | 'none'
  rewardVaultRewardSymbol?: string
}

const AprCard = ({ farm, rewardVaultRewardApr, stakingType, rewardVaultRewardSymbol }: AprCardProps) => {
  const theme = useContext(ThemeContext)
  // Only get farmApr if we have a farm and stakingType is farm
  const { apr: farmApr } = useKodiakFarmAverageApr(stakingType === 'farm' ? farm : null)

  // Determine which APR value and label to show based on the stakingType
  let aprValue = null
  let aprLabel = 'APR'

  if (stakingType === 'farm' && farm) {
    aprValue = farmApr
    aprLabel = 'Farm APR'
  } else if (stakingType === 'vault') {
    aprValue = rewardVaultRewardApr
    aprLabel = `${rewardVaultRewardSymbol || 'Reward'} APR`
  }

  return (
    <S.IslandInfoInnerBox>
      <ThemedText.DeprecatedBody fontSize="14px" fontWeight="500" lineHeight="14px" color={theme.textSecondary}>
        {aprLabel}
      </ThemedText.DeprecatedBody>

      <ThemedText.DeprecatedBody
        fontSize="20px"
        fontWeight="400"
        lineHeight="28px"
        color={theme.textPrimary}
        style={{
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        }}
      >
        {aprValue === null ? <Loader /> : `${formatAmount(aprValue.toFixed(2))}%`}
      </ThemedText.DeprecatedBody>
    </S.IslandInfoInnerBox>
  )
}

function WrongNetworkCard() {
  const theme = useContext(ThemeContext)
  return (
    <>
      <S.Wrapper>
        <AutoColumn gap="lg" justify="center">
          <AutoColumn gap="lg" style={{ width: '100%' }}>
            <TitleRow style={{ marginTop: '1rem' }} padding="0">
              <ThemedText.DeprecatedBody fontSize="20px">
                <>Islands Overview</>
              </ThemedText.DeprecatedBody>
            </TitleRow>

            <MainContentWrapper>
              <ErrorContainer>
                <ThemedText.DeprecatedBody color={theme.deprecated_text3} textAlign="center">
                  <NetworkIcon strokeWidth={1.2} />
                  <div data-testid="pools-unsupported-err">
                    <>Your connected network is unsupported.</>
                  </div>
                </ThemedText.DeprecatedBody>
              </ErrorContainer>
            </MainContentWrapper>
          </AutoColumn>
        </AutoColumn>
      </S.Wrapper>
      <PoweredByKodiak />
    </>
  )
}

interface GenericFarmsPageProps {
  farmAddress?: Address
  vaultAddress?: Address
  originalFarmAddress?: Address
  isWhitelisted?: boolean
  isVaultView: boolean
}

export default function GenericFarmsPage({
  farmAddress,
  vaultAddress,
  originalFarmAddress,
  isWhitelisted = false,
  isVaultView = false,
}: GenericFarmsPageProps) {
  const { account, chainId } = useConnection()
  const theme = useContext(ThemeContext)

  // Farm Data
  const { isLoading, data: kodiakFarm } = useKodiakFarm(farmAddress)

  const {
    isLoading: isLoadingVault,
    data: rewardVault,
    tvl: rewardVaultTVL,
    apr: rewardVaultRewardApr,
  } = useRewardVaultDatas(vaultAddress)
  // Determine active staking mechanism
  const isVaultActive = !!vaultAddress && isVaultView
  const isFarmActive = !isVaultActive && !!kodiakFarm

  const { isAdmin } = useIsFarmAdmin(farmAddress || '0x')

  // Token & Currency Setup
  const token = useToken(isVaultActive ? rewardVault?.stakingToken : kodiakFarm?.stakingToken.id)

  const currency = useMemo(() => (token ? unwrappedToken(token) : null), [token])

  // User token balance
  const userTokenBalance = useTokenBalance(account, token as Token, REFETCH_POLICY_FAST)

  // Farm data
  const { isLoading: isLoadingFarmLockedStakes, data: farmLockedStakesData } = useKodiakFarmLockedStakesOf(
    isFarmActive ? kodiakFarm : null,
    account
  )

  const { isLoading: isLoadingFarm, totalLiquidityUSD: farmTVL } = useKodiakFarmTotalLocked(
    isFarmActive ? kodiakFarm : undefined,
    kodiakFarm?.id
  )

  // Combine data from both sources based on what's active
  const totalFarmLiquidity = isFarmActive ? farmLockedStakesData?.totalLiquidity : undefined
  const totalUserFarmLiquidityUSD = isFarmActive ? farmLockedStakesData?.totalLiquidityUSD : undefined
  const stakes = isFarmActive ? farmLockedStakesData?.stakes || [] : []

  // Check for migration scenario - user has farm position but token is whitelisted for vault
  const userHasFarmPosition =
    farmLockedStakesData?.totalLiquidity && farmLockedStakesData.totalLiquidity.greaterThan('0')
  const shouldShowMigration = isWhitelisted && isVaultActive && userHasFarmPosition && !!originalFarmAddress
  const rewardVaultRewardSymbol = rewardVault?.rewardToken?.symbol

  // Format the stakes data for the PositionCard component
  const stakesForPositionCard = useMemo(() => {
    if (isFarmActive) {
      // Use the existing farm stakes array for Farm
      return stakes || []
    } else if (isVaultActive && rewardVault && rewardVault.positions) {
      // Return the positions array from vault data
      return rewardVault.positions
    }
    return [] // Return empty array if neither is active
  }, [isFarmActive, isVaultActive, stakes, rewardVault])

  // Helper function to get correct terminology based on staking type
  const getStakingTerminology = useCallback(
    (farmTerm: string) => {
      if (isVaultActive) {
        // Return Reward Vault equivalent terminology
        switch (farmTerm) {
          case 'Farm Contract':
            return 'Reward Vault Contract'
          case 'Farm TVL':
            return 'Reward Vault TVL'
          case 'Farm APR':
            return `${rewardVaultRewardSymbol || 'Reward'} APR`
          case 'Locked':
            return 'Staked'
          default:
            return farmTerm
        }
      }
      return farmTerm
    },
    [isVaultActive, rewardVaultRewardSymbol]
  )

  // Loading state
  const isPageLoading = isLoadingFarm || isLoading || isLoadingVault || isLoadingFarmLockedStakes || !currency

  if (account && !isSupportedChain(chainId)) {
    return <WrongNetworkCard />
  }

  if (isPageLoading) {
    return (
      <S.Wrapper>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
          <Loader size="40px" />
        </div>
      </S.Wrapper>
    )
  }

  // If we don't have farm or vault data or token information, show error
  if (!isFarmActive && !isVaultActive) {
    return (
      <S.Wrapper>
        <ErrorWrapper>
          <ThemedText.DeprecatedBody fontSize="20px" fontWeight="500" lineHeight="14px" color={theme.textPrimary}>
            Could not load farm or vault data. Please check the address and try again.
          </ThemedText.DeprecatedBody>
        </ErrorWrapper>
      </S.Wrapper>
    )
  }

  return (
    <>
      <S.Wrapper>
        <StyledInternalLink
          data-cy="back-button"
          style={{ textDecoration: 'none', width: 'fit-content', marginBottom: '0.5rem' }}
          to="/liquidity/farms"
        >
          <S.BackButton>
            <ArrowLeft color={theme.textSecondary} size={20} />
            Farms
          </S.BackButton>
        </StyledInternalLink>

        {shouldShowMigration && <MigrationBanner rewardVaultRewardTokenSymbol={rewardVaultRewardSymbol} />}

        {(kodiakFarm || rewardVault) && currency && (
          <>
            <S.IslandInfoCard>
              <S.IslandSelect
                disabled={false}
                visible={true}
                selected={true}
                hideInput={false}
                className="open-island-select-button"
              >
                <S.FlexRowCenter>
                  <>
                    <span style={{ marginRight: '0.5rem' }}>
                      <CurrencyLogo currency={currency} size="24px" />
                    </span>
                    <ThemedText.DeprecatedBody
                      fontSize="20px"
                      fontWeight="600"
                      lineHeight="20px"
                      color={theme.textPrimary}
                    >
                      {currency.symbol}
                    </ThemedText.DeprecatedBody>
                  </>
                  {isVaultActive && (
                    <StategyBadge>
                      {rewardVaultRewardSymbol ? `${rewardVaultRewardSymbol} Reward Vault` : 'Reward Vault'}
                    </StategyBadge>
                  )}
                  {isFarmActive && <StategyBadge>Kodiak Farm</StategyBadge>}
                </S.FlexRowCenter>
              </S.IslandSelect>

              {chainId && (
                <S.FlexRowCenter>
                  {isFarmActive && kodiakFarm && (
                    <ExternalLink href={getExplorerLink(chainId, farmAddress ?? '', ExplorerDataType.ADDRESS)}>
                      Farm Contract ↗
                    </ExternalLink>
                  )}
                  {isVaultActive && vaultAddress && (
                    <ExternalLink href={getExplorerLink(chainId, vaultAddress, ExplorerDataType.ADDRESS)}>
                      {getStakingTerminology('Farm Contract')} ↗
                    </ExternalLink>
                  )}
                  <ExternalLink href={getExplorerLink(chainId, token?.address ?? '', ExplorerDataType.ADDRESS)}>
                    {token?.symbol} Contract ↗
                  </ExternalLink>
                  {isAdmin && <ExternalLink href="#">Configuration</ExternalLink>}
                </S.FlexRowCenter>
              )}

              <S.FlexRowCenter style={{ flexWrap: 'wrap' }}>
                <S.IslandInfoInnerBox>
                  <ThemedText.DeprecatedBody
                    fontSize="14px"
                    fontWeight="500"
                    lineHeight="14px"
                    color={theme.textSecondary}
                  >
                    {getStakingTerminology('Farm TVL')}
                  </ThemedText.DeprecatedBody>

                  <ThemedText.DeprecatedBody
                    fontSize="20px"
                    fontWeight="400"
                    lineHeight="28px"
                    color={theme.textPrimary}
                  >
                    <FiatValue value={isVaultActive ? rewardVaultTVL : farmTVL} />
                  </ThemedText.DeprecatedBody>
                </S.IslandInfoInnerBox>

                {/* APR Card - Shows Farm APR or reward APR based on active staking type */}
                <AprCard
                  farm={isFarmActive ? kodiakFarm : null}
                  rewardVaultRewardApr={isVaultActive ? rewardVaultRewardApr : null}
                  stakingType={isVaultActive ? 'vault' : isFarmActive ? 'farm' : 'none'}
                  rewardVaultRewardSymbol={rewardVaultRewardSymbol}
                />
              </S.FlexRowCenter>
            </S.IslandInfoCard>

            <S.FlexCol></S.FlexCol>

            <S.AnalyticsAndManagementFlex>
              <S.CardRowFlex>
                <S.FlexCol>
                  <IslandPositionCard
                    totalFarmLiquidityUSD={
                      isFarmActive
                        ? totalUserFarmLiquidityUSD
                        : isVaultActive && rewardVault
                        ? rewardVault.userStakedUSD || 0
                        : undefined
                    }
                    totalLiquidity={
                      isFarmActive
                        ? totalFarmLiquidity
                        : isVaultActive && rewardVault
                        ? rewardVault.selfStaked
                        : undefined
                    }
                    stakingToken={token ?? undefined}
                    stakes={stakesForPositionCard}
                    totalBalance={userTokenBalance}
                    farm={isFarmActive ? kodiakFarm : null}
                    stakingType={isVaultActive ? 'vault' : isFarmActive ? 'farm' : 'none'}
                  />

                  {(isFarmActive || isVaultActive) && (
                    <IslandFarmEarnedCard
                      kodiakFarm={isFarmActive ? kodiakFarm : null}
                      rewardVault={isVaultActive ? rewardVault : undefined}
                      stakingType={isVaultActive ? 'rewardVault' : isFarmActive ? 'kodiakFarm' : 'none'}
                      shouldShowMigration={shouldShowMigration}
                      hasKodiakFarm={true}
                      // farmId={farmAddress ?? ''}
                    />
                  )}
                </S.FlexCol>

                <IslandManagementCard
                  farm={isFarmActive ? kodiakFarm : null}
                  rewardVault={isVaultActive ? vaultAddress : undefined}
                  stakingType={isVaultActive ? 'vault' : isFarmActive ? 'farm' : 'none'}
                  showMigrationOption={shouldShowMigration}
                />
              </S.CardRowFlex>
            </S.AnalyticsAndManagementFlex>
          </>
        )}
      </S.Wrapper>

      <PoweredByKodiak />
    </>
  )
}
